/* Atelier Contact — calligraphic correspondence card. Narrow centered
   column. Italic field labels. Soft rose blush. */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function AtelierContact() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => { document.title = `${studioName} — Write Us`; }, [studioName]);

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email, and a short message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Your letter has been received. We will write back soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The letter did not send — please try again.");
    } finally { setSending(false); }
  }

  return (
    <div data-testid="atelier-contact" style={{ background: "var(--tenant-bg, #FEF5F2)", color: "var(--tenant-fg, #3A1B22)" }}>
      <section className="px-6 pt-20 pb-32">
        <div className="max-w-xl mx-auto text-center">
          <div className="text-[10px] uppercase tracking-[0.42em] mb-5" style={{ color: "var(--tenant-accent)" }}>
            Correspondence
          </div>
          <h1 className="italic" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.25rem, 5vw, 3.5rem)" }}>
            Write us a letter.
          </h1>
          <p className="mt-6 italic text-base leading-loose" style={{ color: "var(--tenant-fg-soft)" }}>
            We reply within two business days. For urgent matters,{" "}
            {settings?.contact_phone && (
              <a href={`tel:${settings.contact_phone}`} className="underline" style={{ color: "var(--tenant-accent)" }}>
                ring us
              </a>
            )}.
          </p>

          <form onSubmit={onSubmit} className="mt-12 space-y-7 text-left">
            {[
              { name: "name", label: "Your name", type: "text" },
              { name: "email", label: "Email", type: "email" },
              { name: "phone", label: "Telephone (optional)", type: "tel" },
            ].map((f) => (
              <div key={f.name}>
                <label htmlFor={`atelier-${f.name}`}
                       className="block italic text-base mb-2"
                       style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-fg-soft)" }}>
                  {f.label}
                </label>
                <input id={`atelier-${f.name}`} type={f.type} value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  data-testid={`atelier-contact-${f.name}`}
                  className="w-full bg-transparent border-b py-2 outline-none focus:border-current italic"
                  style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)", fontFamily: "var(--tenant-serif)" }} />
              </div>
            ))}
            <div>
              <label htmlFor="atelier-message" className="block italic text-base mb-2"
                     style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-fg-soft)" }}>
                Your message
              </label>
              <textarea id="atelier-message" rows={5} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="atelier-contact-message"
                className="w-full bg-transparent border-b py-2 outline-none focus:border-current italic resize-none"
                style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)", fontFamily: "var(--tenant-serif)" }} />
            </div>
            <div className="text-center pt-4">
              <button type="submit" disabled={sending} data-testid="atelier-contact-submit"
                className="inline-flex items-center gap-3 px-10 py-4 text-[11px] uppercase tracking-[0.32em] rounded-full border-2 transition-opacity disabled:opacity-60"
                style={{ borderColor: "var(--tenant-accent)", color: "var(--tenant-accent)" }}>
                {sending ? "Sending..." : "Seal & send →"}
              </button>
            </div>
          </form>

          {(settings?.contact_email || settings?.contact_location) && (
            <div className="mt-16 pt-8 border-t italic text-sm space-y-1"
                 style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 25%, transparent)", color: "var(--tenant-fg-soft)" }}>
              {settings?.contact_location && <div>{settings.contact_location}</div>}
              {settings?.contact_email && (
                <div><a href={`mailto:${settings.contact_email}`} className="underline" style={{ color: "var(--tenant-accent)" }}>{settings.contact_email}</a></div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
